/**
 * Content Studio — admin page that turns any playbook into 5 ready-to-post
 * social captions (X, Facebook, LinkedIn) using Claude Sonnet 4.6.
 *
 * Design intent: turn Tracy's existing playbook library into daily social
 * ammo in ≤ 30 seconds. Zero-typing UX — pick a playbook, click Generate,
 * copy the post you like, paste to the platform. Every generation is fresh
 * (stateless — no DB save yet; can be added later if she wants a "saved
 * drafts" library).
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  Sparkles, Loader2, Copy, Check, ExternalLink, RefreshCw,
  Twitter, Facebook, Linkedin, Wand2,
} from "lucide-react";

const PLATFORM_META = {
  x: { label: "X", icon: Twitter, bg: "#000", fg: "#fff" },
  facebook: { label: "Facebook", icon: Facebook, bg: "#1877F2", fg: "#fff" },
  linkedin: { label: "LinkedIn", icon: Linkedin, bg: "#0A66C2", fg: "#fff" },
};

export default function AdminContentStudio() {
  const [playbooks, setPlaybooks] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState("");
  const [platforms, setPlatforms] = useState({ x: true, facebook: true, linkedin: true });
  const [count, setCount] = useState(5);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/admin/content-studio/playbooks")
      .then(({ data }) => {
        setPlaybooks(data.playbooks || []);
        if ((data.playbooks || []).length > 0) setSelectedSlug(data.playbooks[0].slug);
      })
      .catch((e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load playbooks"));
  }, []);

  const togglePlatform = (key) => setPlatforms((p) => ({ ...p, [key]: !p[key] }));

  const generate = async () => {
    if (!selectedSlug) {
      toast.error("Pick a playbook first");
      return;
    }
    const activePlatforms = Object.entries(platforms).filter(([, v]) => v).map(([k]) => k);
    if (activePlatforms.length === 0) {
      toast.error("Pick at least one platform");
      return;
    }
    setGenerating(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/content-studio/generate", {
        playbook_slug: selectedSlug,
        platforms: activePlatforms,
        count,
      });
      setResult(data);
      toast.success(`Generated ${data.posts?.length || 0} posts — copy the ones you like!`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="admin-content-studio">
      <div>
        <h1 className="font-display text-3xl font-bold flex items-center gap-2">
          <Wand2 className="text-[var(--primary)]" size={28} /> Content Studio
        </h1>
        <p className="text-sm text-[var(--ink-2)] mt-1">
          Turn any playbook into 5 ready-to-post social captions in seconds.
          Pick a playbook, click generate, copy the post you like, paste to the platform. Zero writing.
        </p>
      </div>

      <ControlPanel
        playbooks={playbooks}
        selectedSlug={selectedSlug}
        setSelectedSlug={setSelectedSlug}
        platforms={platforms}
        togglePlatform={togglePlatform}
        count={count}
        setCount={setCount}
        generating={generating}
        onGenerate={generate}
      />

      {generating && (
        <div className="rounded-xl border border-[var(--line)] bg-white p-10 text-center" data-testid="content-studio-generating">
          <Loader2 size={28} className="animate-spin mx-auto text-[var(--primary)] mb-3" />
          <div className="font-semibold">Claude is drafting your posts…</div>
          <div className="text-xs text-[var(--ink-3)] mt-1">Usually 8-15 seconds</div>
        </div>
      )}

      {result && !generating && <ResultPanel result={result} onRegenerate={generate} />}
    </div>
  );
}

function ControlPanel({
  playbooks, selectedSlug, setSelectedSlug,
  platforms, togglePlatform, count, setCount,
  generating, onGenerate,
}) {
  const chosenBook = playbooks.find((p) => p.slug === selectedSlug);
  return (
    <div className="rounded-xl border border-[var(--line)] bg-white p-5 space-y-4" data-testid="content-studio-controls">
      <div>
        <label className="block text-[11px] font-semibold text-[var(--ink-2)] uppercase tracking-widest mb-1.5">
          Playbook
        </label>
        <select
          value={selectedSlug}
          onChange={(e) => setSelectedSlug(e.target.value)}
          className="input w-full"
          data-testid="content-studio-playbook-select"
        >
          {playbooks.map((p) => (
            <option key={p.slug} value={p.slug}>{p.title}</option>
          ))}
        </select>
        {chosenBook && (
          <div className="text-xs text-[var(--ink-3)] mt-1.5">
            <span className="italic">{chosenBook.tagline}</span>
          </div>
        )}
      </div>

      <div>
        <div className="block text-[11px] font-semibold text-[var(--ink-2)] uppercase tracking-widest mb-1.5">
          Platforms
        </div>
        <div className="flex flex-wrap gap-2">
          {Object.entries(PLATFORM_META).map(([key, meta]) => {
            const Icon = meta.icon;
            const active = platforms[key];
            return (
              <button
                key={key}
                type="button"
                onClick={() => togglePlatform(key)}
                className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border transition ${
                  active
                    ? "text-white border-transparent"
                    : "bg-white text-[var(--ink-2)] border-[var(--line)] hover:border-[var(--ink-3)]"
                }`}
                style={active ? { background: meta.bg } : undefined}
                data-testid={`content-studio-platform-${key}`}
              >
                <Icon size={13} /> {meta.label}
                {active && <Check size={11} className="ml-0.5" />}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex items-end gap-3 flex-wrap">
        <div>
          <label className="block text-[11px] font-semibold text-[var(--ink-2)] uppercase tracking-widest mb-1.5">
            Post count
          </label>
          <input
            type="number"
            min="1"
            max="8"
            value={count}
            onChange={(e) => setCount(Math.max(1, Math.min(8, parseInt(e.target.value, 10) || 5)))}
            className="input w-24"
            data-testid="content-studio-count"
          />
        </div>
        <button
          type="button"
          onClick={onGenerate}
          disabled={generating || !selectedSlug}
          className="btn-money"
          data-testid="content-studio-generate-btn"
        >
          {generating ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
          {generating ? "Generating…" : "Generate posts"}
        </button>
      </div>
    </div>
  );
}

function ResultPanel({ result, onRegenerate }) {
  return (
    <div className="space-y-3" data-testid="content-studio-result">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[var(--ink-3)] font-semibold">Generated for</div>
          <div className="font-display font-bold text-lg">{result.playbook_title}</div>
          <a
            href={result.landing_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-[var(--ink-2)] hover:text-[var(--primary-dark)] inline-flex items-center gap-1"
            data-testid="content-studio-landing-link"
          >
            {result.landing_url} <ExternalLink size={10} />
          </a>
        </div>
        <button
          type="button"
          onClick={onRegenerate}
          className="btn-ghost text-xs"
          data-testid="content-studio-regenerate"
        >
          <RefreshCw size={12} /> Regenerate (new variations)
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {result.posts.map((post, idx) => (
          <PostCard key={idx} post={post} index={idx} />
        ))}
      </div>
    </div>
  );
}

function PostCard({ post, index }) {
  const [copied, setCopied] = useState(false);
  const meta = PLATFORM_META[post.platform] || PLATFORM_META.x;
  const Icon = meta.icon;
  const fullText = post.hashtags && post.hashtags.length > 0
    ? `${post.text}\n\n${post.hashtags.join(" ")}`
    : post.text;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(fullText);
      setCopied(true);
      toast.success(`Copied — paste to ${meta.label}`);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — please copy manually.");
    }
  };

  return (
    <div
      className="rounded-xl border border-[var(--line)] bg-white p-4 flex flex-col gap-3 hover:shadow-sm transition"
      data-testid={`content-studio-post-${index}`}
    >
      <div className="flex items-center justify-between">
        <div
          className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2 py-1 rounded-md"
          style={{ background: meta.bg, color: meta.fg }}
        >
          <Icon size={11} /> {meta.label}
        </div>
        <div className="text-[10px] text-[var(--ink-3)] font-mono">
          {post.text.length} chars
        </div>
      </div>
      <div className="text-sm whitespace-pre-wrap leading-relaxed flex-1" data-testid={`content-studio-post-text-${index}`}>
        {post.text}
      </div>
      {post.hashtags && post.hashtags.length > 0 && (
        <div className="text-xs text-[var(--primary-dark)] font-semibold">
          {post.hashtags.join(" ")}
        </div>
      )}
      <button
        type="button"
        onClick={copy}
        className="btn-ghost text-xs justify-center border border-[var(--line)] hover:border-[var(--ink-3)]"
        data-testid={`content-studio-copy-${index}`}
      >
        {copied ? <Check size={12} /> : <Copy size={12} />}
        {copied ? "Copied!" : "Copy to clipboard"}
      </button>
    </div>
  );
}
